/*------------------------------------------------*/
/* LIBRARIES
/*------------------------------------------------*/
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { v4 } from 'uuid';
/*------------------------------------------------*/
/* INTERNAL DEPENDENCIES
/*------------------------------------------------*/
// Components
import Modal from '../Modal/index';
import ModalChild from '../Modal/index.style';
import Icon from '../Icon';
// Styles
import {
  NavLink,
  NavLinkExternal,
} from './HeaderStyles';
import theme from '../../styles/theme';
// Helpers
import { isHelpCenterSection, isBlogSection, isAuthorSection } from '../../utils/pageURLCheck';
import generalHelper from '../../utils/general';


const DropIcon = styled.div`
  display: inline-block;
  margin-left: 7px;
  width: 7px;
  
  svg {
    vertical-align: middle;
    transition: transform 0s linear;
    transform: ${props => props.transform};  
  }
  &.use-dark-mode {
    svg {
      stroke: ${theme.body.dark};
    }
  }
`;

export const NavWrapper = styled.nav`
  background-color: ${theme.navbar.dark};
  color: ${props => (props.useDarkBackground ? theme.body.dark : theme.body.light)};
  display: ${props => (props.isOpen ? 'block' : 'none')};
  height: 100vh;
  left: 0;
  outline: none;
  overflow: hidden;
  padding: ${props => (props.isOpen ? '0 0 2.625rem 3rem' : '2.625rem 3rem')};
  position: absolute;
  top: 0;
  width: 100vw;
  z-index: 3;

  > .scrolling-content {
    overflow: auto;
    height: calc(100vh - 90px);
    margin-top: 90px;
  }

  ul {
    position: relative;
    flex-direction: column;

    &.last-items {
      margin: 0 0 40px 0;
    }
  }

  li {
    display: flex;
    margin: 0.468rem 0rem;
    opacity: ${props => (props.isOpen ? '1' : '0')};
    transition: opacity .5s cubic-bezier(0.000, 0.995, 0.990, 1.000);
  }

  .drop-menu {
    display: flex;
    flex-direction: column;
    margin: 0;
    position: relative;

    :hover {
      cursor: pointer;
    }

    & > div {
      padding: 0 0 0 28px;
    }
  }

  .drop-header {
    &,
    &:hover,
    &:active {
      background: transparent;
      color: ${theme.textColor.white};
    }

    ${DropIcon} {
      display: none;
    }
  }
  
  @media (min-width: 990px) {
    align-items: center;
    background-color: transparent;
    display: flex;
    flex-grow: 1;
    height: unset;
    justify-content: center;
    max-width: 30rem;
    opacity: ${props => (props.searchOpen ? '0' : '1')};
    overflow: initial;
    padding: 0;
    position: unset;
    transform: scale(1);
    transition: opacity 0.1s linear;
    width: 100vw;
    z-index: ${props => (props.hideMenu ? '-1' : '2')};

    > .scrolling-content {
      overflow: initial;
      height: initial;
      margin: initial;
    }

    ul {
      position: relative;
      width: auto;
      display: flex;
      transition: opacity 0.1s linear;
      opacity: ${props => (props.searchOpen ? '0' : '1')};
      flex-direction: row;
      top: 0;
      align-items: baseline;
    }

    li {
      margin: 0 0.4rem;
      opacity: ${props => (props.searchOpen ? '0' : '1')};
      position: relative;
      transition: opacity 0.1s linear;
    }

    .drop-menu {
      & > div {
        padding: 0;
      }
    }

    .drop-header,
    .drop-header:hover,
    .drop-header:active {
      &:not(.use-dark-mode) {
        display: block;
        background: inherit;
        color: ${theme.body.light};
      }

      ${DropIcon} {
        display: inline;
      }
    }
  }
  @media (min-width: 1200px) {
    max-width: 45rem;
  }
`;
NavWrapper.propTypes = {
  hideMenu: PropTypes.bool,
  searchOpen: PropTypes.bool,
  isOpen: PropTypes.bool,
  useDarkBackground: PropTypes.bool,
};

const DropDown = styled.div`
  position: absolute;
  display: ${props => (props.isHover ? 'block' : 'none')};
  top: 35px;
  left: 0;
  background: ${theme.navbar.light};
  box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.078);
  border-radius: 3px;

  & a {
    display: block;
    padding: 12px 15px;
    color: ${theme.body.light};

    &:hover {
      background-color: initial;
    }
  }

  .dropdown-items-wrapper {
    position: relative;
  }
  @media(max-width: 989px) {
    position: relative;
    top: 0;
    height: auto;
    width: 100%;
    display: block;
    background: #1F2432;
    padding: 0;
    margin: 0;
    box-shadow: none;
    border-radius: 0;

    & a:nth-child(1) {
      margin: 0.468rem 0rem;
    }
    & a {
      color: white;
      margin: 0.468rem 0rem;
      padding: 0.66rem 0.5rem 0.66rem 0;
    }
  }
`;


const Nav = ({
  children, isOpen, menu, pathname, title, useDarkBackground, searchOpen
}) => {
  const [isHover, setHover] = useState('');

  if (generalHelper.isWindowUndefined()) {
    return null;
  }

  if (searchOpen) {
    setTimeout(() => {
      const inputElement = document.querySelector('.search-input input');
      if (inputElement) {
        inputElement.focus();
      }
    }, 250);
  }

  // This show a menu for external links:
  if (!isHelpCenterSection(pathname) && !isBlogSection(pathname) && !isAuthorSection(pathname)) {
    return (
      <NavWrapper
        isOpen={isOpen}
        hideMenu={Boolean(title)}
        useDarkBackground={useDarkBackground}
      >
        <div className="scrolling-content">
          {(Boolean(title) && title) || (menu && (
            <ul key={v4()}>
              {menu.map((element) => {
                if (!element.options) {
                  return (
                    <li key={v4()}>
                      <NavLink to={element.url} activeClassName="active">{element.name}</NavLink>
                    </li>
                  );
                }
                return (
                  <li
                    className="drop-menu"
                    onClick={() => setHover(element.name)}
                    aria-hidden="true"
                    key={v4()}
                  >
                    <NavLinkExternal className={`drop-header ${useDarkBackground ? 'use-dark-mode' : ''}`}>
                      {element.name}
                      <DropIcon
                        alt="drop-icon"
                        transform={(isHover === element.name) ? 'rotate(180deg)' : ''}
                        className={`${useDarkBackground ? 'use-dark-mode' : ''}`}
                      >
                        <Icon name="dropArrow" />
                      </DropIcon>
                    </NavLinkExternal>
                    <DropDown isHover={(isHover === element.name)}>
                      <div className="dropdown-items-wrapper">
                        {element.options.map(subElement => (
                          <NavLinkExternal
                            href={subElement.url}
                            key={v4()}
                          >
                            {subElement.name}
                          </NavLinkExternal>
                        ))}
                      </div>
                    </DropDown>
                  </li>
                );
              })}
            </ul>
          ))}
          {children}
          {Boolean(isHover) && (
            <Modal>
              <ModalChild
                onClick={() => { setHover(''); }}
              />
            </Modal>
          )}
        </div>
      </NavWrapper>
    );
  }

  // This shows a menu for internal links:
  // This only shows 3 items, and after that it shows a "Show more" item with the remaining ones:
  const ITEMS_LIMIT = 3;
  return (
    <>
      <NavWrapper
        isOpen={isOpen}
        hideMenu={Boolean(title)}
        searchOpen={searchOpen}
        useDarkBackground={useDarkBackground}
      >
        <div className="scrolling-content">
          {(Boolean(title) && title) || (menu && (
            <ul key={v4()}>
              {menu.map((element, i) => {
                if (i <= ITEMS_LIMIT) {
                  return (
                    <li key={v4()}>
                      <NavLink
                        to={element.url}
                        className={useDarkBackground ? 'use-dark-mode' : ''}
                        activeClassName="active"
                      >
                        {element.name}
                      </NavLink>
                    </li>
                  );
                }
                return true;
              })}
              {(menu.length > (ITEMS_LIMIT + 1)) && (
                <li
                  className="drop-menu"
                  onClick={() => setHover('more')}
                  aria-hidden="true"
                  key={v4()}
                >
                  <NavLinkExternal
                    className={useDarkBackground ? 'drop-header use-dark-mode' : 'drop-header'}
                  >
                    More
                    <DropIcon
                      alt="drop-icon"
                      transform={(isHover === 'more') ? 'rotate(180deg)' : ''}
                    >
                      <Icon name="dropArrow" color={useDarkBackground ? `${theme.body.dark}` : `${theme.body.light}`} />
                    </DropIcon>
                  </NavLinkExternal>
                  <DropDown isHover={(isHover === 'more')}>
                    <div className="dropdown-items-wrapper">
                      {menu.map((element, i) => {
                        if (i > ITEMS_LIMIT) {
                          return (
                            <NavLink
                              onClick={() => setHover('')}
                              to={element.url}
                              activeClassName="active"
                              key={v4()}
                            >
                              {element.name}
                            </NavLink>
                          );
                        }
                        return true;
                      })}
                    </div>
                  </DropDown>
                </li>
              )}
            </ul>
          ))}
          {children}
          {Boolean(isHover) && (
            <Modal>
              <ModalChild
                onClick={() => { setHover(''); }}
              />
            </Modal>
          )}
        </div>
      </NavWrapper>
    </>
  );
};


/*------------------------------------------------*/
/* PROP TYPES
/*------------------------------------------------*/
Nav.defaultProps = {
  isOpen: false,
  searchOpen: false,
  children: '',
  title: '',
  menu: [{
    url: '/',
    name: 'Defaul Name',
  }],
  useDarkBackground: false,
};
Nav.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  children: PropTypes.any,
  isOpen: PropTypes.bool,
  searchOpen: PropTypes.bool,
  title: PropTypes.string,
  useDarkBackground: PropTypes.bool,
  pathname: PropTypes.string.isRequired,
  menu: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      name: PropTypes.string,
    })
  ),
};

/*------------------------------------------------*/
/* EXPORTS
/*------------------------------------------------*/
export default Nav;
